import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { List, CalendarDays, Columns3 } from 'lucide-react';
import { toast } from '../../../lib/toast';
import { API, auth, errMsg, PrimaryBtn, Modal, Field, inputCls } from '../finance/shared';
import { EmptyState } from '../../../components/dashboard/TodaySchedule';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';
import Icon from '../../../components/dashboard/DashboardIcons';
import { statusMeta } from './bookingStatusMeta';
import { PaymentMethodBadge } from './PaymentMethodBadge';
import PaymentProofModal from './PaymentProofModal';
import MonthCalendarView from './MonthCalendarView';
import WeekCalendarView from './WeekCalendarView';

const todayIso = () => new Date().toISOString().slice(0, 10);
const BLANK = { start_time: '', end_time: '', status: 'booked', court_id: '', customer_name: '', customer_phone: '', notes: '' };
const CANCELLABLE = ['booked', 'pending', 'pending_payment', 'pending_payment_verification'];

export default function BookingsCalendarTab({ venues, canManage }) {
  const [venueId, setVenueId] = useState(venues?.[0]?.id || '');
  const [date, setDate] = useState(todayIso());
  const [view, setView] = useState('list');
  const [bookings, setBookings] = useState(null);
  const [courts, setCourts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [proofBooking, setProofBooking] = useState(null);

  useEffect(() => { if (!venueId && venues?.length) setVenueId(venues[0].id); }, [venues, venueId]);

  const load = useCallback(async () => {
    if (!venueId) { setBookings([]); return; }
    try {
      const res = await axios.get(`${API}/venues/${venueId}/bookings`, { ...auth(), params: { date } });
      setBookings(res.data || []);
    } catch { setBookings([]); }
  }, [venueId, date]);

  const loadCourts = useCallback(async () => {
    if (!venueId) { setCourts([]); return; }
    try {
      const res = await axios.get(`${API}/venues/${venueId}/courts`, auth());
      setCourts(res.data || []);
    } catch { setCourts([]); }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCourts(); }, [loadCourts]);

  const courtLabel = (courtId) => courts.find((c) => c.id === courtId)?.label;
  const activeVenue = venues.find((v) => v.id === venueId);
  const goToDay = (d) => { setDate(d); setView('list'); };

  const openCreate = () => { setEditing(null); setForm(BLANK); setModalOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ start_time: b.start_time, end_time: b.end_time, status: b.status, court_id: b.court_id || '', customer_name: b.customer_name || '', customer_phone: b.customer_phone || '', notes: b.notes || '' });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.start_time || !form.end_time) { toast.error('حدد وقت البداية والنهاية'); return; }
    setSaving(true);
    const payload = { ...form, date, court_id: form.court_id || null };
    try {
      if (editing) await axios.put(`${API}/venues/${venueId}/bookings/${editing.id}`, payload, auth());
      else await axios.post(`${API}/venues/${venueId}/bookings`, payload, auth());
      toast.success(editing ? 'تم تحديث الحجز' : 'تمت إضافة الحجز بنجاح');
      setModalOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل حفظ الحجز')); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/venues/${venueId}/bookings/${confirmDelete.id}`, auth());
      toast.success('تم حذف الحجز');
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل الحذف')); }
  };

  const act = async (booking, action, successMsg) => {
    try {
      await axios.post(`${API}/venues/${venueId}/bookings/${booking.id}/${action}`, {}, auth());
      toast.success(successMsg);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل تنفيذ الإجراء')); }
  };

  if (!venues?.length) {
    return <EmptyState icon="calendar" text="أضف ملعباً أولاً من تبويب الملاعب لتتمكن من جدولة الحجوزات" testId="calendar-no-venues" />;
  }

  return (
    <div data-testid="bookings-calendar-tab">
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="venue-pills">
        {venues.map((v) => (
          <button
            key={v.id} onClick={() => setVenueId(v.id)} data-testid={`venue-pill-${v.id}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${venueId === v.id ? 'bg-[#16233b] text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            {v.name}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-56"><PremiumDatePicker value={date} onChange={setDate} testId="calendar-date-picker" /></div>
          <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="calendar-view-switch">
            <button onClick={() => setView('list')} data-testid="calendar-view-list" title="قائمة" className={`rounded-md p-1.5 transition-colors ${view === 'list' ? 'bg-[#16233b] text-white' : 'text-slate-500 hover:bg-slate-50'}`}><List className="h-4 w-4" /></button>
            <button onClick={() => setView('month')} data-testid="calendar-view-month" title="تقويم شهري" className={`rounded-md p-1.5 transition-colors ${view === 'month' ? 'bg-[#16233b] text-white' : 'text-slate-500 hover:bg-slate-50'}`}><CalendarDays className="h-4 w-4" /></button>
            <button onClick={() => setView('week')} data-testid="calendar-view-week" title="تقويم أسبوعي" className={`rounded-md p-1.5 transition-colors ${view === 'week' ? 'bg-[#16233b] text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Columns3 className="h-4 w-4" /></button>
          </div>
        </div>
        {canManage && view === 'list' && (
          <PrimaryBtn onClick={openCreate} data-testid="booking-add-btn"><Icon name="plus" className="h-4 w-4" /> حجز جديد</PrimaryBtn>
        )}
      </div>

      {view === 'month' && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <MonthCalendarView venueId={venueId} onSelectDay={goToDay} />
        </div>
      )}

      {view === 'week' && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <WeekCalendarView venueId={venueId} operatingHours={activeVenue?.operating_hours} onSelectDay={goToDay} />
        </div>
      )}

      {view === 'list' && (bookings === null ? (
        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : bookings.length === 0 ? (
        <EmptyState icon="clock" text="لا توجد حجوزات في هذا اليوم لهذا الملعب" testId="bookings-empty" />
      ) : (
        <div className="space-y-2" data-testid="bookings-list">
          {bookings.map((b) => {
            const meta = statusMeta(b.status);
            return (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`booking-row-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {b.booking_number && <span dir="ltr" data-testid={`booking-number-${b.id}`} className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-mono font-bold text-white">{b.booking_number}</span>}
                  <span dir="ltr" className="font-mono text-sm font-bold text-slate-800">{b.start_time} – {b.end_time}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${meta.badge}`}>{meta.label}</span>
                  {b.court_id && courtLabel(b.court_id) && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{courtLabel(b.court_id)}</span>}
                  {b.payment_method && <PaymentMethodBadge methodId={b.payment_method} testId={`booking-payment-method-${b.id}`} />}
                  {b.source === 'public' && <span className="rounded-full bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-500">طلب من الموقع</span>}
                </div>
                <div className="flex-1 text-sm text-slate-600">
                  {b.customer_name && <span className="font-semibold text-slate-800">{b.customer_name}</span>}
                  {b.customer_phone && <span dir="ltr" className="mr-2"> {b.customer_phone}</span>}
                  {b.notes && <span className="text-slate-400"> — {b.notes}</span>}
                </div>
                {canManage && (
                  <div className="flex flex-wrap gap-1.5">
                    {b.status === 'pending' && (
                      <>
                        <button onClick={() => act(b, 'approve', 'تم قبول طلب الحجز')} data-testid={`booking-approve-${b.id}`} className="rounded-lg border border-teal-200 px-2.5 py-1.5 text-xs font-bold text-teal-700 hover:bg-teal-50">قبول</button>
                        <button onClick={() => act(b, 'reject', 'تم رفض طلب الحجز')} data-testid={`booking-reject-${b.id}`} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">رفض</button>
                      </>
                    )}
                    {(b.status === 'pending_payment' || b.status === 'pending_payment_verification') && (
                      <>
                        {b.status === 'pending_payment_verification' && (
                          <>
                            <button onClick={() => setProofBooking(b)} data-testid={`booking-view-proof-${b.id}`} className="rounded-lg border border-blue-200 px-2.5 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-50">عرض الإثبات</button>
                            <button onClick={() => act(b, 'reject-proof', 'تم رفض الإثبات')} data-testid={`booking-reject-proof-${b.id}`} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">رفض الإثبات</button>
                          </>
                        )}
                        <button onClick={() => act(b, 'confirm-payment', 'تم تأكيد استلام الدفع')} data-testid={`booking-confirm-payment-${b.id}`} className="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50">تأكيد استلام الدفع</button>
                      </>
                    )}
                    {(b.status === 'booked' || b.status === 'blocked') && (
                      <button onClick={() => openEdit(b)} data-testid={`booking-edit-${b.id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">تعديل</button>
                    )}
                    {CANCELLABLE.includes(b.status) && (
                      <button onClick={() => act(b, 'cancel', 'تم إلغاء الحجز')} data-testid={`booking-cancel-${b.id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">إلغاء</button>
                    )}
                    <button onClick={() => setConfirmDelete(b)} data-testid={`booking-delete-${b.id}`} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">حذف</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <Modal open={modalOpen} title={editing ? 'تعديل الحجز' : 'حجز جديد'} onClose={() => setModalOpen(false)} wide>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="وقت البداية" required><input type="time" className={inputCls} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="booking-form-start" /></Field>
          <Field label="وقت النهاية" required><input type="time" className={inputCls} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="booking-form-end" /></Field>
          {courts.length > 0 && (
            <Field label="المقاس/الكورت">
              <select className={inputCls} value={form.court_id} onChange={(e) => setForm({ ...form, court_id: e.target.value })} data-testid="booking-form-court">
                <option value="">بلا تحديد مقاس</option>
                {courts.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
          )}
          <Field label="الحالة">
            <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="booking-form-status">
              <option value="booked">محجوز</option>
              <option value="blocked">محظور/صيانة</option>
            </select>
          </Field>
          <Field label="اسم العميل"><input className={inputCls} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="booking-form-customer-name" /></Field>
          <Field label="رقم الجوال"><input className={inputCls} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="booking-form-customer-phone" /></Field>
          <div className="sm:col-span-2">
            <Field label="ملاحظات"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-form-notes" /></Field>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">إلغاء</button>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="booking-form-submit">{saving ? 'جاري الحفظ...' : 'حفظ الحجز'}</PrimaryBtn>
        </div>
      </Modal>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="booking-delete-confirm-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6">
            <h4 className="font-bold text-slate-900">تأكيد الحذف</h4>
            <p className="mt-2 text-sm text-slate-500">سيتم حذف هذا الحجز نهائياً. هل أنت متأكد؟</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
              <button onClick={remove} data-testid="booking-delete-confirm-btn" className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white">تأكيد الحذف</button>
            </div>
          </div>
        </div>
      )}

      {proofBooking && <PaymentProofModal venueId={venueId} booking={proofBooking} onClose={() => setProofBooking(null)} />}
    </div>
  );
}
